import { useCallback, useMemo, useState } from "react"
import { useMutation, useQueryClient, type DefaultError } from "@tanstack/react-query"
import { useDispatch } from "react-redux"
import { useGetQuery } from "hooks/queries"
import { del, getPatErrorMessage, isForbidden, post } from "helpers/pat-api"
import { useNotifications } from "hooks/notifications"
import { useAccountAlias } from "resources/account"
import type {
  AccountPatListResponse,
  AccountPatCreateResponse,
  AccountPatRevealResponse,
  AccountPatRotateResponse,
  AccountPatMember,
  CreateAccountPatInput,
  PatPendingState,
} from "../types"
import { patSlotKey } from "../types"

const ACCOUNT_PAT_KEY_ROOT = "account-personal-access-tokens"

const EMPTY_MEMBERS: AccountPatMember[] = []

function withKey(keys: Set<string>, key: string): Set<string> {
  const next = new Set(keys)
  next.add(key)
  return next
}

function withoutKey(keys: Set<string>, key: string): Set<string> {
  const next = new Set(keys)
  next.delete(key)
  return next
}

export function useAccountPats() {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const notifs = useNotifications()
  const account = useAccountAlias()

  // The alias is a path segment here, unlike the self-service hook where the account rides
  // as a `?account=` query param on `/api/user/pat` — this route is already account-scoped
  // by `:alias`, so it scopes the cache the same way every other `/api/${account}/...`
  // resource in this codebase does (see resources/portfolios.ts).
  const basePath = `/api/${account}/pat`
  const patKey = [ACCOUNT_PAT_KEY_ROOT, account]

  // No account means no answer to "which account's members and tokens?", so don't ask — an
  // unguarded fetch would request `/api/undefined/pat` and cache the response under that key.
  const query = useGetQuery<AccountPatListResponse>({
    url: basePath,
    key: patKey,
    enabled: !!account,
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: patKey })

  // A 403 here means the server-side state (the feature flag, the account off-switch, or a
  // member's write entitlement) no longer matches what the client last fetched. Re-fetching
  // lets `patEnabled`/`canCreateReadWrite` come back current so the view self-heals (e.g. hides
  // a Create control) instead of leaving controls that fail identically on every click. Other
  // transient errors don't imply stale server state, so they're left alone.
  const onMutationError = (error: unknown) => {
    notifs.error(getPatErrorMessage(error))
    if (isForbidden(error)) invalidate()
  }

  // `gcTime: 0` on the three mutations whose result carries a plaintext secret. While the view
  // is mounted the observer suppresses collection either way, so this does not shorten the live
  // window; it drops the settled entry as soon as the observer detaches instead of holding the
  // secret in the mutation cache — and in ReactQueryDevtools — for the default five minutes.
  // `revokeMutation` returns no body, so it keeps the default.
  const createMutation = useMutation<AccountPatCreateResponse, DefaultError, CreateAccountPatInput>({
    mutationFn: input => post<AccountPatCreateResponse>(dispatch, basePath, input),
    onSuccess: invalidate,
    onError: onMutationError,
    gcTime: 0,
  })

  const revealMutation = useMutation<AccountPatRevealResponse, DefaultError, string>({
    mutationFn: id => post<AccountPatRevealResponse>(dispatch, `${basePath}/${id}/reveal`),
    onError: onMutationError,
    gcTime: 0,
  })

  const rotateMutation = useMutation<AccountPatRotateResponse, DefaultError, string>({
    mutationFn: id => post<AccountPatRotateResponse>(dispatch, `${basePath}/${id}/rotate`),
    onSuccess: invalidate,
    onError: onMutationError,
    gcTime: 0,
  })

  const revokeMutation = useMutation<unknown, DefaultError, string>({
    mutationFn: id => del(dispatch, `${basePath}/${id}`),
    onSuccess: invalidate,
    onError: onMutationError,
  })

  // A mutation acts on one slot, so only that slot's controls go dead. The profile card can
  // use a single account-wide flag because it shows one user; this grid shows every member, so
  // the same flag would freeze hundreds of unrelated rows for the length of one request.
  // Copy, rotate and revoke name the token they act on; create names the member and scope it
  // is about to fill, because the slot holds no token yet.
  //
  // Tracked here rather than read off each mutation's `variables`, which holds only the newest
  // call: `MutationObserver.mutate` detaches the observer from the previous mutation and builds
  // a fresh one, so a second reveal (or rotate, or revoke) on another token would drop the
  // first token back to enabled while its request was still running.
  const [pendingTokenIds, setPendingTokenIds] = useState<Set<string>>(new Set<string>())
  const [pendingCreates, setPendingCreates] = useState<Set<string>>(new Set<string>())

  const createToken = useCallback((input: CreateAccountPatInput) => {
    const key = patSlotKey(input.userId, input.type)
    setPendingCreates(keys => withKey(keys, key))
    return createMutation.mutateAsync(input).finally(() => setPendingCreates(keys => withoutKey(keys, key)))
  }, [createMutation.mutateAsync])

  const trackToken = useCallback(<T>(id: string, run: (id: string) => Promise<T>): Promise<T> => {
    setPendingTokenIds(ids => withKey(ids, id))
    return run(id).finally(() => setPendingTokenIds(ids => withoutKey(ids, id)))
  }, [])

  const revealToken = useCallback(
    (id: string) => trackToken(id, revealMutation.mutateAsync),
    [trackToken, revealMutation.mutateAsync],
  )
  const rotateToken = useCallback(
    (id: string) => trackToken(id, rotateMutation.mutateAsync),
    [trackToken, rotateMutation.mutateAsync],
  )
  const revokeToken = useCallback(
    (id: string) => trackToken(id, revokeMutation.mutateAsync),
    [trackToken, revokeMutation.mutateAsync],
  )

  const pending = useMemo<PatPendingState>(
    () => ({ tokenIds: pendingTokenIds, creates: pendingCreates }),
    [pendingTokenIds, pendingCreates],
  )

  return {
    members: query.data?.members ?? EMPTY_MEMBERS,
    // True whenever there is no response to read (in flight, or failed) so the view survives
    // to show its error and Retry instead of flashing a false "PATs are off" banner. Once a
    // response exists, an absent or unexpected flag reads as disabled: the same fail-closed
    // polarity the profile card uses, so the two readers cannot disagree about whether the
    // account allows PATs.
    patEnabled: query.data ? query.data.patEnabled === true : true,
    // `isPending` rather than `isLoading` so a query that hasn't started (no account yet)
    // reads the same as one in flight. `isLoading` is false while disabled, which would let
    // the view render its empty state with live Create buttons before we know the answer.
    isPending: query.isPending,
    isError: query.isError,
    // Retry needs this, and `isPending` cannot stand in for it. `refetch` resets `status` to
    // "pending" only while the query holds no data; once a mutation has invalidated and the
    // refetch fails, `status` stays "error" and only `fetchStatus` moves, so without this the
    // button would give no sign it had been clicked.
    isFetching: query.isFetching,
    pending,
    refetch: query.refetch,
    createToken,
    revealToken,
    rotateToken,
    revokeToken,
  }
}

export default useAccountPats
